/**
 * Global motion behaviours, one custom element each.
 *
 * Every constant here is measured from the Athora reference storefront. Each
 * element is a no-op under `prefers-reduced-motion`, and the pointer-driven ones
 * are additionally gated on a real hover-capable pointer — a magnetic cursor on
 * a touch screen is dead weight, not a degraded experience.
 */
import { animate, scroll, hover, frame, motionValue, styleEffect, resize } from '@theme/motion';
import { prefersReducedMotion } from '@theme/utilities';

const canAnimate = () => !prefersReducedMotion();
const canHover = () => matchMedia('(hover: hover) and (pointer: fine)').matches;
const withPageScroller = (options) => options;

/* ------------------------------------------------------------------
 * <nuvrya-effect-img eff="parallax|ambient">
 *
 * parallax: the image is oversized and drifted through its own frame as the
 *   frame crosses the viewport. Scale and travel are locked together so the
 *   image never exposes an edge — travel is derived from scale, not guessed.
 * ambient: a slow orbital drift for decorative media that never scrolls.
 * ---------------------------------------------------------------- */
class EffectImage extends HTMLElement {
  #stop;

  connectedCallback() {
    this.image = this.querySelector('img, video, svg');
    if (!this.image) return;

    const effect = this.getAttribute('eff') || 'none';
    if (effect === 'none' || !canAnimate()) return;

    if (effect === 'ambient') this.#ambient();
    else if (effect === 'parallax') this.#parallax();
  }

  disconnectedCallback() {
    this.#stop?.();
  }

  #ambient() {
    const animation = animate(
      this.image,
      {
        transform: [
          'rotate(0deg) translateX(1em) rotate(0deg) scale(1.2)',
          'rotate(360deg) translateX(1em) rotate(-360deg) scale(1.2)',
        ],
      },
      { duration: 30, ease: 'linear', repeat: Infinity }
    );
    this.#stop = () => animation.stop();
  }

  #parallax() {
    const scale = Number.parseFloat(this.style.getPropertyValue('--parallax-scale')) || 1.3;

    // Keep translation within the image's actual overscan when a section uses
    // a smaller scale to preserve faces and garment silhouettes.
    const travel = (Math.min(0.15, Math.max(0, (scale - 1) / 2)) * 100) / scale;

    // A first-in-page section is already at the top of the scroll range, so it
    // has no "enters the viewport" phase to scrub against.
    const isFirst = this.closest('.shopify-section')?.matches(':first-child');

    this.#stop = scroll(
      animate(
        this.image,
        {
          transform: [
            `scale(${scale}) translateY(-${travel}%)`,
            `scale(${scale}) translateY(${travel}%)`,
          ],
        },
        { ease: 'linear' }
      ),
      withPageScroller({ target: this.image, offset: [isFirst ? 'start start' : 'start end', 'end start'] })
    );
  }
}

/* ------------------------------------------------------------------
 * <nuvrya-marquee speed="60" direction="ltr|rtl" pausable>
 *
 * Clones its content until it spans three container widths, then translates by
 * exactly one span. Three, not two: at two the seam is inside the viewport on
 * wide screens. Duration is derived from distance so speed stays constant
 * regardless of how much content is inside.
 * ---------------------------------------------------------------- */
class Marquee extends HTMLElement {
  #animation;
  #stopResize;
  #lastWidth = 0;

  connectedCallback() {
    this.inner = this.querySelector('[marquee-inner]') || this.firstElementChild;
    if (!this.inner?.firstElementChild) return;

    this.speed = Number.parseFloat(this.getAttribute('speed')) || 60;
    this.direction =
      this.getAttribute('direction') || (document.documentElement.dir === 'rtl' ? 'rtl' : 'ltr');

    this.#setup();
    this.#stopResize = resize(this, () => this.#setup());

    if (this.hasAttribute('pausable') && canAnimate()) {
      this.addEventListener('mouseenter', () => this.#animation?.pause());
      this.addEventListener('mouseleave', () => this.#animation?.play());
    }
  }

  disconnectedCallback() {
    this.#lastWidth = 0;
    this.#animation?.stop();
    this.#stopResize?.();
  }

  #setup() {
    const firstWidth = this.inner.firstElementChild?.offsetWidth ?? 0;
    if (firstWidth === 0 || this.#lastWidth === firstWidth) return;
    this.#lastWidth = firstWidth;

    for (const clone of this.querySelectorAll('[marquee-clone]')) clone.remove();

    const clonesNeeded = Math.ceil((this.offsetWidth * 3) / firstWidth);
    const fragment = document.createDocumentFragment();

    for (let i = 0; i < clonesNeeded; i++) {
      const clone = this.inner.firstElementChild.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('marquee-clone', '');
      // Duplicated content must not be reachable by keyboard.
      for (const focusable of clone.querySelectorAll('a, button')) focusable.tabIndex = -1;
      fragment.appendChild(clone);
    }

    this.inner.append(fragment);

    if (!canAnimate()) return;

    const distance = this.inner.scrollWidth / 3;
    const duration = distance / this.speed;
    const from = this.direction === 'rtl' ? '0px' : `-${distance}px`;
    const to = this.direction === 'rtl' ? `-${distance}px` : '0px';

    this.#animation?.stop();
    this.#animation = animate(
      this.inner,
      { transform: [`translateX(${from}) translateZ(0)`, `translateX(${to}) translateZ(0)`] },
      { duration, ease: 'linear', repeat: Infinity }
    );
  }
}

/* ------------------------------------------------------------------
 * <nuvrya-shrink-on-scroll mode="expand|contract" mobile="false">
 *
 * The hero is clipped inward by `--clip-x` on both sides with rounded corners,
 * and opens to full bleed as it arrives (expand) or closes back down as it
 * leaves (contract). Overlay content translates by the same `--clip-x` so it
 * stays optically centred inside the clipped area rather than drifting off it —
 * the copy and the frame move as one object, which is the whole trick.
 *
 * Requires a `.nuvrya-shrink__inner` child. Elements carrying
 * `data-shrink-translate="start|end"` ride along; the direction mirrors in RTL.
 * ---------------------------------------------------------------- */
const CLIP_INSET = 'inset(0 var(--clip-x) 0 var(--clip-x) round var(--clip-radius, var(--rounded-lg)))';
const CLIP_FULL_SQUARE = 'inset(0 round 0)';
const CLIP_FULL_ROUND = 'inset(0 round var(--rounded-lg))';

class ShrinkOnScroll extends HTMLElement {
  #stops = [];
  #stopResize;

  connectedCallback() {
    this.inner = this.querySelector('.nuvrya-shrink__inner');
    this.#init();
    this.#stopResize = resize(this, () => {
      this.#teardown();
      frame.render(() => this.#init());
    });
  }

  disconnectedCallback() {
    this.#teardown();
    this.#stopResize?.();
  }

  get #isMobile() {
    return !matchMedia('(min-width: 768px)').matches;
  }

  get #expands() {
    return (this.getAttribute('mode') || 'expand') === 'expand';
  }

  #teardown() {
    for (const stop of this.#stops) stop?.();
    this.#stops = [];
    if (this.inner) this.inner.style.clipPath = '';
    for (const el of this.querySelectorAll('[data-shrink-translate], [data-shrink-translate-mb]')) {
      el.style.transform = '';
    }
  }

  #init() {
    if (!canAnimate()) return;
    // `mobile="false"` is how the hero opts out of the effect on phones, where
    // the section already fills the viewport and has nothing to expand into.
    if (this.#isMobile && this.getAttribute('mobile') === 'false') return;

    const expand = this.#expands;
    const offset = expand ? ['0 1', '1 0.7'] : ['1 0.8', '1 0.1'];

    if (this.inner) {
      const removeRound = this.getAttribute('remove-round') !== 'false';
      const from = expand ? CLIP_INSET : removeRound ? CLIP_FULL_SQUARE : CLIP_FULL_ROUND;
      const to = expand ? (removeRound ? CLIP_FULL_SQUARE : CLIP_FULL_ROUND) : CLIP_INSET;

      // The reference does not map scroll straight onto the clip. It runs a
      // 300ms linear tween toward the scroll-derived value, so the shape
      // settles into place instead of tracking the wheel one-to-one — that
      // lag is most of why its scroll reads as smooth rather than mechanical.
      // Measured on `hdt-shrink-on-sroll-inner`, `hdt-hero-bg-animate` and
      // `hdt-media-content`, all 300ms linear.
      //
      // Driven here by following the scroll progress with a time-based decay
      // rather than by animating per frame, so the settle is frame-rate
      // independent. If anything about the paused-animation API is missing the
      // code falls back to the direct mapping it used before.
      const smoothed = this.#smoothClip(from, to, offset);
      if (!smoothed) {
        this.#stops.push(
          scroll(animate(this.inner, { clipPath: [from, to] }), withPageScroller({ offset, target: this }))
        );
      }
    }

    this.#initTranslates(expand, offset);
  }

  /** Follows scroll progress with a 300ms settle. Returns false if the
   *  animation cannot be driven manually, so the caller can fall back. */
  #smoothClip(from, to, offset) {
    let animation;
    try {
      animation = animate(this.inner, { clipPath: [from, to] }, { duration: 1, ease: 'linear' });
      animation.pause();
      if (typeof animation.time !== 'number') return false;
    } catch (error) {
      return false;
    }

    // 300ms to close the gap; the decay constant is that settle expressed so
    // the follower is independent of how often frames actually land.
    const settle = 300;
    let target = 0;
    let current = 0;
    let last = performance.now();
    let running = true;

    const step = (now) => {
      if (!running) return;
      const dt = Math.min(64, now - last);
      last = now;
      current += (target - current) * (1 - Math.exp((-dt * 3) / settle));
      animation.time = current;
      requestAnimationFrame(step);
    };
    requestAnimationFrame(step);

    const stopScroll = scroll((progress) => {
      target = progress;
    }, withPageScroller({ offset, target: this }));

    this.#stops.push(() => {
      running = false;
      stopScroll?.();
      animation.cancel?.();
    });

    return true;
  }

  #initTranslates(expand, offset) {
    const attribute = this.#isMobile ? 'data-shrink-translate-mb' : 'data-shrink-translate';
    const rtl = document.documentElement.dir === 'rtl';

    for (const el of this.querySelectorAll('[data-shrink-translate], [data-shrink-translate-mb]')) {
      if (!el.hasAttribute(attribute)) continue;

      let direction = (el.getAttribute(attribute) || '').trim();
      if (!direction) continue;
      if (rtl) direction = direction === 'start' ? 'end' : 'start';

      // Travel is expressed against the same --clip-x the mask uses, so the two
      // stay locked together at any viewport width.
      const offsetValue =
        direction === 'start'
          ? 'calc(var(--clip-x) * 1)'
          : direction === 'end'
            ? 'calc(var(--clip-x) * -1)'
            : '0px';

      const from = expand ? offsetValue : '0px';
      const to = expand ? '0px' : offsetValue;

      this.#stops.push(
        scroll(animate(el, { transform: [`translateX(${from})`, `translateX(${to})`] }), withPageScroller({
          offset,
          target: this,
        }))
      );
    }
  }
}

/* ------------------------------------------------------------------
 * <nuvrya-morph-footer reveal-on-scroll>
 *
 * The footer logo arrives as the footer does. Desktop only — on a phone the
 * footer is most of the viewport by the time it is reached, so there is no
 * approach to animate.
 * ---------------------------------------------------------------- */
class MorphFooter extends HTMLElement {
  #stop;

  connectedCallback() {
    const logo = this.querySelector('.nuvrya-footer__logo');
    if (!this.hasAttribute('reveal-on-scroll') || !logo) return;
    if (!canAnimate() || !matchMedia('(min-width: 768px)').matches) return;

    const opacity = motionValue(0);
    const x = motionValue('-100%');
    styleEffect(logo, { opacity, x });

    // Hold still for the first tenth, then run the whole travel across the
    // remainder — the logo should land as the footer settles, not before.
    const START = 0.1;

    this.#stop = scroll(
      (progress) => {
        const p = Math.max(0, (progress - START) / (1 - START));
        opacity.set(p);
        x.set(`${(1 - p) * -100}%`);
      },
      withPageScroller({ target: this, offset: ['start end', 'end end'] })
    );
  }

  disconnectedCallback() {
    this.#stop?.();
  }
}

/* ------------------------------------------------------------------
 * <nuvrya-tilt-card max-tilt="10">
 * ---------------------------------------------------------------- */
class TiltCard extends HTMLElement {
  #controller;
  #tilt = { rotateX: 0, rotateY: 0, z: 0 };

  connectedCallback() {
    if (!canAnimate() || !canHover()) return;

    this.maxTilt = Number.parseFloat(this.getAttribute('max-tilt')) || 10;
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('pointerenter', this.#update, { signal });
    this.addEventListener('pointermove', this.#update, { signal });
    this.addEventListener(
      'pointerleave',
      () => {
        this.#tilt = { rotateX: 0, rotateY: 0, z: 0 };
        frame.postRender(this.#apply);
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  #update = (event) => {
    const rect = this.getBoundingClientRect();
    const xPercent = (event.clientX - rect.left) / rect.width;
    const yPercent = (event.clientY - rect.top) / rect.height;

    this.#tilt = {
      z: -10,
      rotateX: this.maxTilt * (0.5 - yPercent),
      rotateY: this.maxTilt * (xPercent - 0.5),
    };

    frame.postRender(this.#apply);
  };

  #apply = () => {
    animate(this, { transformPerspective: 500, ...this.#tilt });
  };
}

/* ------------------------------------------------------------------
 * <nuvrya-cursor> and <nuvrya-cursor-hover>
 *
 * One cursor element for the document; any number of hover targets that switch
 * its state. Skipped entirely without a fine pointer.
 * ---------------------------------------------------------------- */
class CursorEffect extends HTMLElement {
  #x = motionValue(0);
  #y = motionValue(0);
  #originX = 0;
  #originY = 0;
  #controller;
  #bound = false;

  get disabled() {
    return !canAnimate() || !canHover();
  }

  connectedCallback() {
    if (this.disabled) return;

    document.documentElement.classList.add('nuvrya-cursor-on');
    this.#controller = new AbortController();

    const { top, left, width, height } = this.getBoundingClientRect();
    this.#originX = left + width / 2;
    this.#originY = top + height / 2;

    styleEffect(this, { x: this.#x, y: this.#y });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    document.documentElement.classList.remove('nuvrya-cursor-on');
  }

  /** Bound lazily: no pointer listener until something actually wants the cursor. */
  bind() {
    if (this.disabled || this.#bound) return;
    document.addEventListener(
      'mousemove',
      (event) => {
        this.#x.set(event.clientX - this.#originX);
        this.#y.set(event.clientY - this.#originY);
      },
      { signal: this.#controller.signal }
    );
    this.#bound = true;
  }

  setState(active, type = '') {
    this.classList.toggle('is-active', active);
    if (active) this.setAttribute('type', type);
    else this.removeAttribute('type');
  }
}

class CursorHover extends HTMLElement {
  #stop;

  connectedCallback() {
    const cursor = document.querySelector('nuvrya-cursor');
    if (!cursor || cursor.disabled) return;

    cursor.bind();
    const target = this.hasAttribute('parent') ? this.parentElement : this;
    const type = this.getAttribute('type') || 'go';

    this.#stop = hover(target, () => {
      cursor.setState(true, type);
      return () => cursor.setState(false);
    });
  }

  disconnectedCallback() {
    this.#stop?.();
  }
}

/* ------------------------------------------------------------------
 * <nuvrya-circle-bg mousemove="10">
 *
 * The ink-fill behind a button. Sits in the button's lowest layer, scales up on
 * hover, and follows the pointer by a few pixels so the fill feels attached to
 * the hand rather than to the box.
 * ---------------------------------------------------------------- */
class CircleBackground extends HTMLElement {
  #stop;
  #controller;

  connectedCallback() {
    const button = this.closest('[circle-bg-wrap]') || this.parentElement;
    if (!button || !canAnimate() || !canHover()) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#stop = hover(button, (_, event) => {
      // The fill starts where the pointer crossed the edge, not at the centre.
      // That single detail is what makes the ink read as being pushed in by the
      // hand: enter from the bottom-left and it blooms from the bottom-left.
      const rect = button.getBoundingClientRect();
      const x = (event?.clientX ?? rect.left + rect.width / 2) - rect.left;
      const y = (event?.clientY ?? rect.top + rect.height / 2) - rect.top;
      this.style.left = `${x}px`;
      this.style.top = `${y}px`;

      // Reach the furthest corner from that entry point — never a fixed
      // diagonal, because an off-centre origin needs more radius, not the same.
      // The divisor is the circle's own radius, read rather than assumed: this
      // theme's root font size is 16px, so a hard-coded 10 undershot by 60% and
      // left a corner of the button unfilled.
      const radius = (this.offsetWidth || 20) / 2;
      const scale =
        Math.max(
          Math.hypot(x, y),
          Math.hypot(rect.width - x, y),
          Math.hypot(x, rect.height - y),
          Math.hypot(rect.width - x, rect.height - y)
        ) / radius;

      animate(this, { scale }, { duration: 0.6, ease: [0.22, 1, 0.36, 1] });

      return () => {
        animate(this, { scale: 0 }, { duration: 0.4, ease: [0.22, 1, 0.36, 1] });
        if (magnet) {
          button.style.setProperty('--magnet-x', '0px');
          button.style.setProperty('--magnet-y', '0px');
        }
      };
    });

    // `mousemove="N"` makes the button itself magnetic — the box leans up to N
    // pixels toward the cursor. It moves the button, not the fill; moving the
    // fill instead only slides the ink around inside a box that never reacts.
    const magnet = this.hasAttribute('mousemove');
    if (!magnet) return;

    const shift = Number.parseFloat(this.getAttribute('mousemove')) || 6;
    button.addEventListener(
      'pointermove',
      (event) => {
        const rect = button.getBoundingClientRect();
        const dx = (event.clientX - (rect.left + rect.width / 2)) / (rect.width / 2);
        const dy = (event.clientY - (rect.top + rect.height / 2)) / (rect.height / 2);
        button.style.setProperty('--magnet-x', `${(dx * shift).toFixed(2)}px`);
        button.style.setProperty('--magnet-y', `${(dy * shift).toFixed(2)}px`);
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#stop?.();
    this.#controller?.abort();
  }
}

/* ------------------------------------------------------------------
 * Ink-fill auto-wiring
 *
 * The element above was only ever placed by hand, and it had reached four
 * buttons in the whole theme — so almost every button in the store fell back to
 * a flat colour swap. Buttons are generated in dozens of sections, many of them
 * Horizon's own, and hand-editing each one would leave the next new section
 * behind again.
 *
 * So the wiring happens once, here, over anything that is actually a filled or
 * outlined button. Unstyled buttons, close buttons and steppers are left alone:
 * an ink-fill on a bare icon reads as a bug, not as polish.
 * ---------------------------------------------------------------- */
const INK_SELECTOR = [
  'a.button',
  'button.button',
  '.button-secondary',
  '.nuvrya-button',
].join(',');

const INK_EXCLUDE = [
  '.button-unstyled',
  '.close-button',
  '[circle-bg-wrap]',
  '.quantity__button',
  '.nuvrya-button--ghost',
].join(',');

function wireInkFill(root = document) {
  if (!canHover() || !canAnimate()) return;

  for (const button of root.querySelectorAll?.(INK_SELECTOR) ?? []) {
    if (button.matches(INK_EXCLUDE) || button.querySelector('nuvrya-circle-bg')) continue;

    button.setAttribute('circle-bg-wrap', '');
    const circle = document.createElement('nuvrya-circle-bg');
    circle.setAttribute('aria-hidden', 'true');
    button.appendChild(circle);
  }
}

if (typeof document !== 'undefined') {
  const start = () => {
    wireInkFill();
    // Sections re-render on their own in the theme editor, and drawers and
    // quick-view mount their buttons long after load, so keep watching.
    new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches?.(INK_SELECTOR)) wireInkFill(node.parentNode ?? document);
          else wireInkFill(node);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
}

/* ------------------------------------------------------------------
 * <nuvrya-circular-text aria-label="…">
 * ---------------------------------------------------------------- */
class CircularText extends HTMLElement {
  connectedCallback() {
    const circle = this.querySelector('.nuvrya-circular-text__circle');
    if (!circle || circle.childElementCount) return;

    let text = (this.getAttribute('aria-label') || '').toUpperCase();
    // A trailing space keeps the last and first glyph from colliding on the ring.
    if (text.includes(' ')) text += ' ';

    const chars = [...text];
    const step = 360 / chars.length;
    const fragment = document.createDocumentFragment();

    chars.forEach((char, i) => {
      const span = document.createElement('span');
      span.setAttribute('aria-hidden', 'true');
      span.textContent = char === ' ' ? ' ' : char;
      span.style.setProperty('--char-angle', `${(i * step).toFixed(4)}deg`);
      fragment.appendChild(span);
    });

    circle.appendChild(fragment);
  }
}

const REGISTRY = {
  'nuvrya-effect-img': EffectImage,
  'nuvrya-marquee': Marquee,
  'nuvrya-shrink-on-scroll': ShrinkOnScroll,
  'nuvrya-morph-footer': MorphFooter,
  'nuvrya-tilt-card': TiltCard,
  'nuvrya-cursor': CursorEffect,
  'nuvrya-cursor-hover': CursorHover,
  'nuvrya-circle-bg': CircleBackground,
  'nuvrya-circular-text': CircularText,
};

for (const [tag, constructor] of Object.entries(REGISTRY)) {
  if (!customElements.get(tag)) customElements.define(tag, constructor);
}

export {
  EffectImage,
  Marquee,
  ShrinkOnScroll,
  MorphFooter,
  TiltCard,
  CursorEffect,
  CursorHover,
  CircleBackground,
  CircularText,
};
